import React, {useState, useEffect} from 'react';
import _ from 'lodash';
import {SparklesIcon} from '@heroicons/react-v2/16/solid';

import DropdownInput from '../components/DropdownInput';
import Button, {ButtonTypes} from '../components/ButtonNew';
import Link from '../components/Link';
import CloseButton from '../components/CloseButton';
import {FadeOut} from '../components/FadeOut';
import {Spinner} from '../components/Spinner';
import {SearchIcon} from '../components/svg';
import {getQuestions, getTags} from '../api/api';
import {Feature} from '../../../lib/constants/tags';
import {cx, createURLWithParams} from '../util';

export const dropdownOptionsStyle = {
  top: 46,
  width: 300,
  maxWidth: 300,
  maxHeight: 300,
};

const QuestionSuggestions = ({
  initialRole,
  initialCompany,
  initialCategory,
  onSelectQuestion,
}) => {
  const [role, setRole] = useState(initialRole);
  const [company, setCompany] = useState(initialCompany);
  const [category, setCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState();
  const [tags, setTags] = useState();
  const [questions, setQuestions] = useState({
    pagination: {
      hasMore: false,
      current: 1,
      limit: 0,
      count: 0,
    },
    results: [],
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize
  useEffect(() => {
    getTags({
      feature: Feature.Questions,
    }).then((newTags) => {
      setTags(newTags);
      setIsInitialized(true);
    });
  }, []);

  // Run search
  useEffect(() => {
    setIsLoading(true);
    getQuestions({
      ...role && {role},
      ...company && {company},
      ...category && {type: category},
      ...searchQuery && {query: searchQuery},
    }).then(({results, pagination}) => {
      setQuestions({
        results,
        pagination,
        count: pagination.count,
      });
      setIsLoading(false);
    });
  }, [role, company, category, searchQuery]);

  // Handle search queries with debounced (delayed) input
  const onSearchDebounced = _.debounce(setSearchQuery, 750, {maxWait: 2000});

  if (!isInitialized) return null;

  const tagToOption = (tag) => ({value: tag.id, label: tag.name});
  const roles = tags.role.filter((r) => r.count).map(tagToOption);
  const companies = tags.company.filter((c) => c.count).map(tagToOption);
  const categories = tags.type.filter((t) => t.count).map(tagToOption);
  const seeMoreQuestionsURL = createURLWithParams('/questions', {
    page: 2, role, company, type: category, query: searchQuery,
  });

  return (
    <>
      {/* Search Bar */}
      <div className="mb-2 relative">
        <input
          className="w-full rounded-lg shadow-sm border border-gray-300 pl-10 py-2.5 leading-3 focus:outline-none focus:border-gray-500"
          placeholder="Search for questions, companies..."
          onChange={(e) => onSearchDebounced(e.target.value)}
        />
        <SearchIcon className="absolute m-auto top-0 bottom-0 left-3 h-5 w-5 pointer-events-none" />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap">
        <DropdownInput
          placeholder="Role"
          options={roles}
          value={role}
          onChange={setRole}
          showFilter
          className="mr-2 mb-2"
          optionsStyles={dropdownOptionsStyle}
        />
        <DropdownInput
          placeholder="Company"
          options={companies}
          value={company}
          onChange={setCompany}
          showFilter
          className="mr-2 mb-2"
          optionsStyles={dropdownOptionsStyle}
        />
        <DropdownInput
          placeholder="Category"
          options={categories}
          value={category}
          onChange={setCategory}
          showFilter
          className="mr-2 mb-2"
          optionsStyles={dropdownOptionsStyle}
        />
      </div>

      {/* Results */}
      <div className="text-indigo text-sm font-semibold select-none">
        <SparklesIcon className="h-4 w-4 mr-1" />
        Suggested questions
      </div>

      {isLoading && (
        <div className="pt-2.5 overflow-hidden">
          {[1, 2, 3].map((count) => (
            <div key={count} className="mb-2 p-4 border border-gray-200 rounded-lg bg-white">
              <div className="h-6 bg-gray-200 rounded animate-pulse mb-1" />
              <div className="flex items-end justify-between">
                <div className="w-32 h-4 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && questions.results?.length > 0 && (
        <div className="grow overflow-hidden">
          <FadeOut top>
            <div className="pt-2.5 pb-20">
              {questions.results?.map((question) => (
                <div className="mb-2 p-4 border border-gray-200 rounded-lg bg-white" key={question.id}>
                  <h3 className="text-gray-800 text-base font-semibold leading-tight mr-3 mb-1">
                    {question.data.title}
                  </h3>
                  <div className="flex items-end justify-between">
                    <Link
                      href={`/questions/${question.id}/${question.slug}`}
                      target="_blank"
                      className="text-gray-500 text-xs font-semibold whitespace-nowrap"
                    >
                      View full question →
                    </Link>

                    <Button
                      buttonType={ButtonTypes.TERTIARY}
                      onClick={() => onSelectQuestion(question)}
                      className="-mx-3 -my-2"
                    >
                      Select
                    </Button>
                  </div>
                </div>
              ))}

              {/* TODO: Not a great UX solution currently because user
             has no way of selecting questions shown on later pages */}
              {questions?.pagination?.hasMore && (
              <div className="flex items-center justify-center mt-8">
                <Link
                  href={seeMoreQuestionsURL}
                  className="text-indigo font-semibold text-sm"
                  target="_blank"
                >
                  See more questions
                </Link>
              </div>
              )}
            </div>
          </FadeOut>
        </div>
      )}

      {!isLoading && !(questions.results?.length > 0) && (
        <div className="my-24 flex flex-col items-center justify-center">
          <div className="text-gray-800 text-base font-semibold">
            Sorry, no results found.
          </div>
          <div className="w-72 text-center text-sm text-gray-500 font-medium">
            Try adjusting the filters or search for a different question.
          </div>
        </div>
      )}
    </>
  );
};

export const SlideIn = ({
  onClose,
  isOpen,
  children,
}) => (
  <div className={cx('fixed left-0 top-0 bottom-0 w-1/3 max-w-md min-h-0 flex flex-col z-20 px-5 pt-8 bg-white transform transition-transform duration-300 ease-in-out shadow-lg', {
    'translate-x-0': isOpen,
    '-translate-x-full': !isOpen,
  })}
  >
    <CloseButton
      onClick={onClose}
      className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
    />

    <h3 className="text-gray-800 text-xl font-semibold leading-tight mr-3 mb-1">
      Select a question to ask
    </h3>

    <div className="mb-6 text-sm text-gray-500 font-medium">
      Use our recommended question or browse and select another
      one if your partner has already seen it.
    </div>

    {children}
  </div>
);

export const SuggestedQuestion = ({
  question,
  onShowMore,
  className,
}) => (
  <div className={className}>
    <div className="mb-2 text-sm font-semibold select-none">
      <SparklesIcon className="h-4 w-4 mr-1" />
      Suggested question to ask
    </div>
    {question ? (
      <Link href={`/questions/${question.id}/${question.slug}`} target="_blank">
        <div className="mb-2 p-4 border border-gray-200 rounded-lg bg-white">
          <h3 className="text-gray-800 text-base font-semibold leading-tight mr-3 mb-0.5">
            {question.data.title}
          </h3>
          <span className="text-gray-500 text-xs font-semibold whitespace-nowrap">
            View full question →
          </span>
        </div>
      </Link>
    ) : (
      <div className="flex items-center justify-center h-24">
        <Spinner />
      </div>
    )}
    <div className="flex">
      <Button
        className="flex-1"
        onClick={onShowMore}
      >
        Try a different question
      </Button>
    </div>
  </div>
);

export default QuestionSuggestions;
